import { useEffect, useRef, useState } from 'react';
import { Button, List, Placeholder } from '@telegram-apps/telegram-ui';

import type { AuthFailureReason } from '../api/errors.js';
import { getAuthDateSec } from '../lib/authDate.js';
import { env } from '../env.js';
import {
  closeMiniAppAvailable,
  closeMiniAppSafe,
  openTelegramLinkAvailable,
  openTelegramLinkSafe,
} from '../lib/tg.js';
import {
  isStorageAvailable,
  markRestart,
  readLaunchContext,
  resolveStartParam,
} from '../lib/launchContext.js';
import { useT } from '../hooks/useT.js';

/**
 * Честный экран «пропуск Telegram больше не принимают» — work-038 (§6).
 *
 * Показывается вместо содержимого приложения, когда сервер ответил 401 на любой
 * запрос (единый перехват — `lib/sessionExpiry.ts`). До work-038 такой отказ
 * выглядел как молчание или как ложь: `MyQuestionsScreen` рисовал «ждём ответа»,
 * `SettingsScreen` — включённый тумблер уведомлений.
 *
 * ПОЧЕМУ ПЕРЕЗАПУСК — ЕДИНСТВЕННОЕ ЛЕЧЕНИЕ. Telegram выдаёт `initData` один раз
 * при запуске; метода «обновить пропуск» в Mini Apps API не существует (сверено
 * по полному списку методов SDK 3.11.8), события об обновлении тоже нет.
 * Перезагрузка страницы не помогает: SDK держит launch params в `sessionStorage`
 * (`tapps/launchParams`) и возвращает ТОТ ЖЕ старый пропуск.
 *
 * СВОЕГО CSS НЕТ СОЗНАТЕЛЬНО (§6.1). `DESIGN.md:195` требует описывать новый
 * компонент блок-правилом, а `Placeholder`/полноэкранной высоты там нет. Чтобы
 * не выдумывать значения и не расширять скоуп, экран собран из той же связки
 * `<List><Placeholder action={<Button size="m">}/></List>`, что и success/banned
 * состояния `AskScreen` (`AskScreen.tsx:243-268`). Ни одного hex, `dvh`, `font-size`.
 *
 * CANON D1/SP3 не ослаблены: единственная граница доверия — серверный 401.
 * Здесь только UX вокруг уже полученного отказа.
 */
export interface SessionExpiredScreenProps {
  /** Причина отказа с сервера (плоское поле `code` тела 401). */
  reason: AuthFailureReason;
}

/** Что экран показывает сейчас. `manualClose` — состояние после провала обеих попыток. */
type Mode = 'auto' | 'manualClose';

/**
 * Сколько ждём фактического перезапуска, прежде чем признать, что его не будет.
 * Настоящий перезапуск уничтожает страницу за десятки миллисекунд; 2.5 с — с
 * большим запасом на медленный клиент, но не настолько долго, чтобы человек
 * успел решить, что приложение зависло.
 */
const REOPEN_GRACE_MS = 2500;

/**
 * Собрать ссылку возврата `https://t.me/<bot>?startapp=<сохранённый контекст>`.
 *
 * `undefined`, если имя бота не задано на сборке (`VITE_BOT_USERNAME`
 * необязательна) — тогда кнопка деградирует до простого закрытия мини-аппа.
 * Формат `?startapp` без значения при пустом контексте — как в
 * `apps/api/src/lib/deepLink.ts:41`.
 */
function buildReturnLink(): string | undefined {
  const bot = env.VITE_BOT_USERNAME;
  if (!bot) return undefined;
  const startParam = resolveStartParam();
  return startParam
    ? `https://t.me/${bot}?startapp=${encodeURIComponent(startParam)}`
    : `https://t.me/${bot}?startapp`;
}

export function SessionExpiredScreen({ reason }: SessionExpiredScreenProps): JSX.Element {
  const t = useT();

  /**
   * «Залип ли пропуск» — считаем ОДИН раз на mount.
   *
   * Мы сами пометили прошлый перезапуск (`markRestart`) и запомнили `auth_date`
   * до него. Если после перезапуска пропуск тот же — Telegram отдал закешированный
   * (баг Telegram Desktop #28303, закрыт как «not planned»). Предлагать «открыть
   * заново» второй раз бессмысленно: получится карусель без выхода. Показываем
   * другой текст — как закрыть приложение полностью через меню бота.
   */
  const [stuck] = useState(() => {
    const restart = readLaunchContext()?.restart;
    if (restart?.authDate === undefined) return false;
    const current = getAuthDateSec();
    return current !== undefined && current === restart.authDate;
  });

  /**
   * Можно ли вообще что-то сделать по кнопке — считаем ЗАРАНЕЕ, а не по факту
   * клика. Вне Telegram (`reason: 'no-initdata'`) обе операции SDK недоступны:
   * живая кнопка, которая по нажатию молча ничего не делает, — тупик хуже, чем
   * честный текст «закройте приложение сами».
   */
  const [canReopen] = useState(() => openTelegramLinkAvailable() || closeMiniAppAvailable());

  /**
   * Доступно ли хранилище. Недоступно — значит черновик мы НЕ сохранили и
   * залипший пропуск распознать не сможем; обещать «начатый текст мы сохранили»
   * в этом случае нельзя, вместо этого просим скопировать текст.
   */
  const [storageOk] = useState(() => isStorageAvailable());

  const [mode, setMode] = useState<Mode>('auto');

  /**
   * Страховка от молчаливого тупика: `openTelegramLink` синхронный и `void` —
   * он сообщает, что вызов состоялся, но НЕ что Telegram действительно
   * перезапустил приложение. Если клиент ссылку проигнорировал, экран остался
   * бы прежним и нажатие выглядело бы как «ничего не произошло».
   *
   * Поэтому после успешного вызова заводим таймер: он сработает, только если мы
   * ВСЁ ЕЩЁ здесь — то есть перезапуск не случился. Тогда честно переключаемся
   * на «закройте приложение сами». При реальном перезапуске компонент к этому
   * моменту уже уничтожен и таймер снимается в cleanup.
   */
  const reopenTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(reopenTimerRef.current), []);

  function handleReopen(): void {
    // Метка ставится ДО попытки: по ней следующий запуск поймёт, что перезапуск
    // наш, и разрешит подставить сохранённый `startParam`, а совпавший `auth_date`
    // выдаст залипший пропуск (#28303).
    markRestart(getAuthDateSec());

    const link = buildReturnLink();
    if (link && openTelegramLinkSafe(link) === 'opened') {
      reopenTimerRef.current = setTimeout(() => setMode('manualClose'), REOPEN_GRACE_MS);
      return;
    }

    // Ссылку собрать не удалось (имя бота не задано на сборке) либо открыть её
    // не вышло — закрываем мини-апп: человек откроет его из чата с ботом сам.
    if (closeMiniAppSafe() === 'closed') {
      // Та же страховка: `closeMiniApp` тоже синхронный и без подтверждения.
      reopenTimerRef.current = setTimeout(() => setMode('manualClose'), REOPEN_GRACE_MS);
      return;
    }

    // Обе попытки провалились. Молчаливого «ничего не произошло» быть не должно —
    // объясняем, что закрыть приложение придётся руками.
    setMode('manualClose');
  }

  // Порядок ветвлений важен: первое сработавшее выигрывает (§6.2).
  const view = ((): { header: string; description: string; showButton: boolean } => {
    if (stuck) {
      return {
        header: t.session_expired_stuck_title,
        description: t.session_expired_stuck_desc,
        showButton: false,
      };
    }
    if (mode === 'manualClose' || !canReopen || reason === 'no-initdata') {
      return {
        header: t.session_auth_failed_title,
        description: t.session_expired_manual_close,
        showButton: false,
      };
    }
    if (reason === 'stale') {
      return {
        header: t.session_expired_title,
        // Обещать сохранённый текст можно ТОЛЬКО если хранилище доступно —
        // иначе черновика физически нет, и вместо обещания просим скопировать
        // текст до выхода.
        description: storageOk ? t.session_expired_desc : t.session_expired_desc_no_storage,
        showButton: true,
      };
    }
    // Подпись не сошлась / битый пропуск — перезапуск всё равно уместен, но
    // обещать «мы сохранили текст» тут нечестно: причина не в возрасте.
    return {
      header: t.session_auth_failed_title,
      description: t.session_auth_failed_desc,
      showButton: true,
    };
  })();

  return (
    <List>
      <Placeholder
        header={view.header}
        description={view.description}
        action={
          view.showButton ? (
            <Button size="m" onClick={handleReopen}>
              {t.session_expired_reopen}
            </Button>
          ) : undefined
        }
      />
    </List>
  );
}
